import React, { useEffect, useState } from 'react'
import styles from './CancelSubscriptionModal.module.css'
import NewModal from '../NewModal/NewModal'
import AnimatedButton from '../AnimatedButton/AnimatedButton'

const REASONS = [
  'Too expensive',
  'Not using enough',
  'Missing features',
  'Other reason',
]

const CancelSubscriptionModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  onViewRefundPolicy,
}) => {
  const [reason, setReason] = useState('')
  const [otherText, setOtherText] = useState('')

  /*
   * This component stays mounted and is toggled by `isOpen`, so its state
   * survives a close. Clear it on every open — otherwise reopening shows the
   * previous answer still selected and the guard below is already satisfied on
   * a form the user has not answered this time.
   */
  useEffect(() => {
    if (isOpen) {
      setReason('')
      setOtherText('')
    }
  }, [isOpen])

  /*
   * "Yes, Cancel" requires an actual answer: a reason, plus trimmed text when
   * that reason is "Other reason" (whitespace is not an answer). Same rule as
   * the dashboard's Cancel Subscription modal — same action, so the two
   * surfaces must not disagree.
   */
  const otherFilled = otherText.trim().length > 0
  const canConfirm = !!reason && (reason !== 'Other reason' || otherFilled)

  return (
    <NewModal isOpen={isOpen} onClose={onClose} title={null} height="auto" bodyMaxHeight="90vh">
      <div className={styles.modal}>
        <div className={styles.iconWrap}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13M10 11v6M14 11v6"
              stroke="#EF4444" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h2 className={styles.title}>Cancel Subscription?</h2>
        <p className={styles.desc}>
          Cancelling now will deactivate your agent and release its number. Refund
          eligibility depends on purchase date and usage limits.
        </p>
        <button type="button" className={styles.policyLink} onClick={onViewRefundPolicy}>
          View Refund Policy.
        </button>
        <p className={styles.confirmText}>Are you sure you want to continue?</p>

        <div className={styles.reasonHead}>
          <p className={styles.reasonTitle}>Why are you cancelling?</p>
          <p className={styles.reasonSub}>your feedback helps us improve</p>
        </div>

        <div className={styles.reasonList}>
          {REASONS.map((r) => (
            <label key={r} className={styles.reasonRow}>
              <span className={styles.reasonLabel}>{r}</span>
              <input
                type="radio"
                name="cancelReason"
                className={styles.reasonRadio}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
            </label>
          ))}
        </div>

        {reason === 'Other reason' && (
          <div className={styles.reasonOtherWrap}>
            <textarea
              className={styles.reasonTextarea}
              placeholder="Tell us other reason..."
              maxLength={120}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
            />
            <span className={styles.reasonCounter}>{otherText.length}/120</span>
          </div>
        )}

        <AnimatedButton
          label="Yes, Cancel"
          isLoading={loading}
          disabled={loading || !canConfirm}
          /*
           * Second arg is additive — the current caller
           * (UsageHistory.js) ignores both, so nothing breaks; it means the
           * free-text answer is available whenever this gets wired to a real
           * cancel call.
           */
          onClick={() => onConfirm?.(reason, otherText.trim())}
          positionType="relative"
          width="100%"
          bottom="0"
        />
      </div>
    </NewModal>
  )
}

export default CancelSubscriptionModal
